/** @jsxImportSource @emotion/react */

import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { nike } from '@nike/nike-design-tokens';

import { InvitationsTableRows } from './invitations';
import { UsersTableRows } from './users';
import UserManagementLoadingAnimation from './UserManagementLoadingAnimation';

import { TranslatedCopy } from '../intl/TranslatedCopy';
import { invitationsSelectors } from './invitations/reducer';
import { useDispatchWithProgress } from '../hooks/useDispatchWithProgress';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { usersActions } from './users/actions';
import { usersSelectors } from './users/reducer';

const UserManagementTable = () => {
  const usersActiveReview = useSelector(usersSelectors.activeReview);
  const invitationsActiveReview = useSelector(invitationsSelectors.activeReview);
  const search = useSelector(usersSelectors.search);
  const sort = useSelector(usersSelectors.sort);
  const selectedAccountNumber = useSelector(state => state.user.get('userAccount')?.number);
  const users = useSelector(usersSelectors.usersByIdList);

  const searchUsers = useDispatchWithProgress(usersActions.searchUsers);
  const memoizedSearchUsers = useCallback(
    ([entry]) => {
      if (entry.isIntersecting) {
        searchUsers({
          search,
          sort,
          filters: { accountNumber: selectedAccountNumber, activeOnAccount: usersActiveReview },
          anchor: users.length,
          count: 25
        });
      }
    },
    [search, sort, users.length, selectedAccountNumber]
  );

  const [rootRef, targetRef] = useIntersectionObserver({ callback: memoizedSearchUsers });

  return (
    <table
      css={{
        background: nike.podiumCdsColorBgPrimary,
        border: `1px solid ${nike.podiumCdsColorBorderPrimary}`,
        borderRadius: '8px',
        display: 'block',
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'scroll',
        width: '100%'
      }}
      ref={rootRef}
    >
      <thead
        css={{
          background: nike.podiumCdsColorWhite,
          borderBottom: `1px solid ${nike.podiumCdsColorBorderPrimary}`,
          color: nike.podiumCdsColorTextPrimary,
          display: 'table',
          fontSize: '15px',
          fontWeight: 500,
          lineHeight: '18px',
          position: 'sticky',
          tableLayout: 'fixed',
          textAlign: 'left',
          top: 0,
          width: '100%',
          zIndex: 1,
          '& > th': {
            padding: '16px'
          },
          '@media (min-width: 769px)': {
            fontSize: '16px'
          }
        }}
      >
        {!invitationsActiveReview && (
          <th
            css={{
              display: 'table-cell',
              width: '42%',
              '@media (min-width: 769px)': {
                display: 'table-cell',
                width: '27%'
              }
            }}
          >
            <TranslatedCopy id='global.table.name' />
          </th>
        )}
        <th
          css={{
            display: invitationsActiveReview ? 'table-cell' : 'none',
            '@media (min-width: 769px)': {
              display: 'table-cell',
              width: '27%'
            }
          }}
        >
          <TranslatedCopy id='global.form.email' />
        </th>
        <th
          css={{
            width: '16%',
            '@media (min-width: 769px)': {
              width: '20%'
            }
          }}
        >
          <TranslatedCopy id='userManagement.role' />
        </th>
        <th
          css={{
            width: '42%',
            '@media (min-width: 769px)': {
              width: '13%'
            }
          }}
        >
          <TranslatedCopy id='userManagement.table.status' />
        </th>
        {!invitationsActiveReview && (
          <th
            css={{
              display: 'none',
              '@media (min-width: 769px)': {
                display: 'table-cell',
                width: '13%'
              }
            }}
          >
            <TranslatedCopy id='userManagement.table.lastLogin' />
          </th>
        )}
      </thead>
      <tbody
        css={{
          color: nike.podiumCdsColorTextPrimary,
          display: 'table',
          fontSize: '15px',
          height: '437px', // Table height - table head
          position: 'relative',
          tableLayout: 'fixed',
          textAlign: 'left',
          width: '100%',
          '@media (min-width: 769px)': {
            fontSize: '16px',
            height: '495px' // Table height - table head
          }
        }}
      >
        <UsersTableRows targetRef={targetRef} />
        <InvitationsTableRows />
        <UserManagementLoadingAnimation />
      </tbody>
    </table>
  );
};

export default UserManagementTable;
